import type { InMemoryDocument } from "@/lib/document-resource";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { BoxHeader } from "@/components/design/BoxHeader";
import { TextArea, TextInput } from "@/components/design/TextField";
import { AskAssistantButton } from "@/components/chat/AskAssistantButton";
import { FileDropZone } from "@/components/common/FileDropZone";
import { DocumentTile } from "../ProjectDocuments";
import { DocumentPickerDialog, type PickedDocument } from "@/components/share/DocumentPickerDialog";
import { PROJECT_DOCUMENT_ACCEPT } from "@/lib/upload-limits";
import { ProjectImportTiles } from "../ProjectImportTiles";

// Creating a Project needs a title. Everything below the title is optional and
// stays optional. Only title and description reach onCreate; file names are
// local preview state, and assistant generation is not wired yet.
//
// The context block wears the scoped-AI recipe (bg-page box-flat
// smycles-ai-shadow, UI_CONVENTIONS "Where AI lives"): its content is what a
// model would read, so it carries the AI signal instead of an icon.
//
// Zwei bewusste Wiederverwendungen statt eigener Bausteine:
// - Die Ablagefläche ist `FileDropZone` aus components/common, dieselbe, die
//   Save · Dokumente benutzt. Eine zweite Upload-Optik nur für diese Seite wäre
//   genau das, was "Reuse First" in den UI_CONVENTIONS verbietet.
// - Der Generieren-Knopf ist `AskAssistantButton`
//   (@adr SPARK-0035-assistant-hand-offs-use-the-shared-button@2): er trägt den
//   Smike-Glyph und dreht den Kopf des Maskottchens zum Chat. Vorerst
//   `disabled`, die Verdrahtung kommt später.

export interface ProjectDraft {
  title: string;
  description: string;
  /**
   * Der Assistent hat den Kontext vor dem Anlegen gelesen. Heute immer false:
   * der Generieren-Knopf ist noch nicht verdrahtet, das Feld steht schon, damit
   * der Aufrufer sich später nicht ändern muss.
   */
  derived: boolean;
}

export function ProjectCreateScreen({
  onCreate,
  saving = false,
  error = null,
}: {
  onCreate: (project: ProjectDraft) => void;
  /** Läuft gerade eine echte Mutation gegen genesis. */
  saving?: boolean;
  /** Schon nutzertauglich übersetzt (@adr SPARK-0010-no-raw-server-errors-in-ui@1), wird hier nur angezeigt. */
  error?: string | null;
}) {
  const intl = useIntl();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState<InMemoryDocument[]>([]);
  const [docPicker, setDocPicker] = useState(false);

  const canCreate = title.trim().length > 0;

  const addPicked = (picked: PickedDocument[]) => {
    if (picked.length) setFiles((cur) => [...cur, ...picked]);
  };

  return (
    <form
      className="max-w-3xl"
      onSubmit={(e) => {
        e.preventDefault();
        if (!canCreate) return;
        onCreate({ title: title.trim(), description: description.trim(), derived: false });
      }}
    >
      <p className="smycles-field-meta max-w-[60ch] text-ink">
        <FormattedMessage
          id="project.create.sub"
          defaultMessage="Titel genügt. Alles Weitere ist freiwillig und lässt sich später im Projekt ergänzen."
        />
      </p>

      <label className="mt-5 block">
        <span className="smycles-field-label">
          <FormattedMessage id="project.create.titleLabel" defaultMessage="Titel" />
        </span>
        <TextInput
          id="project-create-title"
          className="mt-1.5"
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={intl.formatMessage({
            id: "project.create.titlePlaceholder",
            defaultMessage: "Wofür brauchst du Leute?",
          })}
        />
      </label>

      <label className="mt-4 block">
        <span className="smycles-field-label">
          <FormattedMessage id="project.create.descLabel" defaultMessage="Beschreibung" />
        </span>
        <TextArea
          id="project-create-description"
          className="mt-1.5"
          rows={5}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={intl.formatMessage({
            id: "project.create.descPlaceholder",
            defaultMessage: "Worum geht es? Zwei Sätze reichen, der Rest wächst mit dem Projekt.",
          })}
        />
      </label>

      <div className="bg-page box-flat smycles-ai-shadow mt-6 p-5">
        <BoxHeader
          title={intl.formatMessage({ id: "project.create.contextTitle", defaultMessage: "Kontext mitgeben" })}
        />
        <p className="mt-2 smycles-field-meta max-w-[62ch] text-ink">
          <FormattedMessage
            id="project.create.contextSub"
            defaultMessage="Wenn du schon etwas hast, nimm es gleich mit. Der Assistent liest daraus Anlass, Anforderungen und erste Rollen. Ohne geht es genauso."
          />
        </p>

        <div className="mt-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="smycles-field-label">
              <FormattedMessage id="project.create.uploadLabel" defaultMessage="Unterlagen" />
            </div>
            <Button variant="secondary" size="sm" onClick={() => setDocPicker(true)}>
              <FormattedMessage id="project.create.fromDocuments" defaultMessage="Aus meinen Dokumenten" />
            </Button>
          </div>
          <FileDropZone
            variant="zone"
            clickToOpen
            multiple
            accept={PROJECT_DOCUMENT_ACCEPT}
            className="mt-2 rounded-md"
            onFiles={(list) =>
              setFiles((cur) => [...cur, ...Array.from(list)])
            }
            label={intl.formatMessage({
              id: "project.create.dropLabel",
              defaultMessage: "Datei hier ablegen oder klicken",
            })}
            hint={intl.formatMessage({
              id: "project.create.dropHint",
              defaultMessage: "max. 10 MB pro Datei",
            })}
          />
          {/* Dieselben Kacheln wie im Dokumente-Reiter. Eine Liste nackter
              Dateinamen unter der Ablagefläche sah aus wie ein Protokoll, nicht
              wie "das liegt jetzt hier" - und dasselbe Dokument sah zwei Schritte
              später völlig anders aus. */}
          {files.length > 0 ? (
            <div className="mt-3 grid grid-cols-[repeat(auto-fill,minmax(9rem,9rem))] gap-3">
              {files.map((f, i) => (
                <DocumentTile
                  key={`${f.name}-${i}`}
                  document={f}
                  onRemove={() => setFiles((cur) => cur.filter((_, index) => index !== i))}
                />
              ))}
            </div>
          ) : null}
        </div>

        <div className="mt-5 smycles-field-label">
          <FormattedMessage id="project.create.importLabel" defaultMessage="Import aus" />
        </div>
        <div className="mt-2">
          <ProjectImportTiles />
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-3">
          <AskAssistantButton
            prompt={intl.formatMessage({
              id: "project.create.generatePrompt",
              defaultMessage: "Leite aus Titel, Beschreibung und den mitgegebenen Unterlagen Anlass, Aufteilung und erste Rollen ab.",
            })}
            label={<FormattedMessage id="project.create.generate" defaultMessage="Für mich generieren" />}
            disabled
          />
        </div>
      </div>

      <DocumentPickerDialog open={docPicker} kind="file" onClose={() => setDocPicker(false)} onPick={addPicked} />

      {error ? <p id="project-create-error" className="mt-4 smycles-field-meta text-ink">{error}</p> : null}

      <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
        <Button id="project-create-submit" type="submit" variant="primary" disabled={!canCreate || saving}>
          {saving ? (
            <FormattedMessage id="project.create.submitting" defaultMessage="Wird angelegt …" />
          ) : (
            <FormattedMessage id="project.create.submit" defaultMessage="Projekt anlegen" />
          )}
        </Button>
      </div>
    </form>
  );
}
